import { ReactNode, useCallback, useEffect, useState } from "react";
import { api, MeResponse } from "../lib/api";
import { InstallAppButton } from "./InstallAppButton";

type Allowed = {
  user: MeResponse["user"];
  csrf: string;
  admin: boolean;
  access: MeResponse["access"];
  staff: MeResponse["staff"];
};

type GateState =
  | { status: "loading" }
  | { status: "auth_required" }
  | { status: "paywall"; pricing: MeResponse["pricing"]; access: MeResponse["access"] }
  | { status: "allowed"; ctx: Allowed }
  | { status: "error"; message: string };

const DEFAULT_PRICING: MeResponse["pricing"] = {
  planId: "crm_autodialer_pro_monthly",
  priceUsd: "297.00",
  billing: "monthly",
  checkoutUrl: "/pricing/?product=crm_autodialer&period=monthly#pay-with-fjam"
};

export function AccessGate({ children }: { children: (ctx: Allowed) => ReactNode }) {
  const [state, setState] = useState<GateState>({ status: "loading" });

  const check = useCallback(async () => {
    try {
      const me = await api.me();
      if (!me.access?.allowed && !me.admin) {
        setState({ status: "paywall", pricing: me.pricing || DEFAULT_PRICING, access: me.access });
        return;
      }
      setState({
        status: "allowed",
        ctx: {
          user: me.user,
          csrf: me.csrfToken || "",
          admin: Boolean(me.admin),
          access: me.access,
          staff: me.staff || null
        }
      });
    } catch (e: unknown) {
      const err = e as { status?: number; message?: string };
      if (err.status === 401) {
        setState({ status: "auth_required" });
        return;
      }
      if (err.status === 402) {
        setState({
          status: "paywall",
          pricing: DEFAULT_PRICING,
          access: {}
        });
        return;
      }
      setState({ status: "error", message: err.message || "Unable to verify access" });
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  if (state.status === "loading") {
    return (
      <div className="gate">
        <div className="gate-card">
          <h1>Falcon CRM Autodialer</h1>
          <p>Checking FalconJA subscription…</p>
        </div>
      </div>
    );
  }

  if (state.status === "auth_required") {
    const returnTo = encodeURIComponent("/crm-autodialer/app/");
    return (
      <div className="gate">
        <div className="gate-card">
          <h1>Sign in required</h1>
          <p>Log in with your FalconJA account to open the CRM Autodialer.</p>
          <div className="row-actions" style={{ marginTop: "1.25rem" }}>
            <a className="btn" href={`/login/?return_to=${returnTo}`}>
              Log in
            </a>
            <a className="btn ghost" href={`/register/?return_to=${returnTo}`}>
              Create account
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (state.status === "paywall") {
    return (
      <div className="gate">
        <div className="gate-card">
          <h1>Subscription required</h1>
          <p>
            Falcon CRM Autodialer is a production dialer for 100+ simultaneous outbound calls with AI
            voice after answer, dialpad single-dial, and campaign autodialing.
          </p>
          <div className="price-tag">
            From US$99 <span>/ week</span>
          </div>
          <ul className="muted" style={{ paddingLeft: "1.1rem", lineHeight: 1.6, marginBottom: "0.5rem" }}>
            <li>
              <strong>Weekly</strong> — US$99
            </li>
            <li>
              <strong>Monthly</strong> — US$297
            </li>
            <li>
              <strong>Yearly</strong> — US$2,970 (save US$594 vs monthly)
            </li>
            <li>Twilio, Telnyx, SIP · ElevenLabs + TTS · dialpad &amp; campaigns</li>
            <li>Site admins already have full access</li>
          </ul>
          <div className="row-actions" style={{ marginTop: "1.25rem" }}>
            <a className="btn" href={state.pricing.checkoutUrl || DEFAULT_PRICING.checkoutUrl}>
              Subscribe · USDT
            </a>
            <a
              className="btn ghost"
              href="/pricing/?product=crm_autodialer&period=weekly#pay-with-fjam"
            >
              Weekly US$99
            </a>
            <a
              className="btn ghost"
              href="/pricing/?product=crm_autodialer&period=yearly#pay-with-fjam"
            >
              Yearly US$2,970
            </a>
            <a className="btn ghost" href="/crm-autodialer/">
              Product details
            </a>
            <a className="btn ghost" href="/account/">
              My account
            </a>
            <InstallAppButton />
          </div>
          <p className="muted" style={{ marginTop: "0.85rem", fontSize: "0.85rem" }}>
            Install as a desktop app (PWA) from Chrome, Edge, or Brave — works offline for the app shell.
          </p>
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="gate">
        <div className="gate-card">
          <h1>Unable to open autodialer</h1>
          <p>{state.message}</p>
          <button className="btn" type="button" onClick={check}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return <>{children(state.ctx)}</>;
}
